<template>
  <v-card-text>
    <!-- Part of Network - First Field -->
    <v-select
      :items="booleanNotSureOptions"
      v-model="localForm.partOfNetwork"
      item-title="name"
      return-object="true"
      variant="outlined"
      :error-messages="getErrorMessages('partOfNetwork').value"
      :is-required="isRequired('partOfNetwork')"
      @blur="v$.partOfNetwork?.$touch()"
      @update:model-value="onPartOfNetworkChange"
    >
      <template #label>
        Is the company part of our network?
        <span v-if="isRequired('partOfNetwork')" class="ml-1 red--text">
          <strong> *</strong>
        </span>
      </template>
    </v-select>

    <!-- If YES: Show Company and Job Title -->
    <template v-if="isPartOfNetworkYes">
      <v-autocomplete
        v-model="localForm.company"
        return-object
        placeholder="Start typing to Search"
        item-title="name"
        item-value="id"
        :items="companies"
        variant="outlined"
        hide-details="auto"
        :menu-props="{ maxWidth: 0 }"
        :error-messages="getErrorMessages('company').value"
        @blur="v$.company?.$touch()"
        @update:model-value="onCompanyChange"
      >
        <template #label>
          Company
          <span v-if="isRequired('company')" class="ml-1 red--text">
            <strong> *</strong>
          </span>
        </template>
      </v-autocomplete>

      <v-autocomplete
        class="pt-5"
        v-model="localForm.jobTitle"
        return-object
        placeholder="Start typing to Search"
        item-title="name"
        item-value="id"
        :items="filteredJobPositions"
        variant="outlined"
        hide-details="auto"
        :menu-props="{ maxWidth: 0 }"
        :error-messages="getErrorMessages('jobTitle').value"
        @blur="v$.jobTitle?.$touch()"
        @update:model-value="updateForm"
      >
        <template #label>
          Job Position
          <span v-if="isRequired('jobTitle')" class="ml-1 red--text">
            <strong> *</strong>
          </span>
        </template>
      </v-autocomplete>
    </template>

    <!-- If NO or NOT_SURE: Show Employer Name and Job Title Text -->
    <template v-else-if="isPartOfNetworkNoOrNotSure">
      <v-text-field
        label="Employer name"
        v-model="localForm.employerName"
        variant="outlined"
        :error-messages="getErrorMessages('employerName').value"
        @blur="v$.employerName?.$touch()"
        @input="updateForm"
      >
        <template #label>
          Employer name
          <span v-if="isRequired('employerName')" class="ml-1 red--text">
            <strong> *</strong>
          </span>
        </template>
      </v-text-field>

      <v-text-field
        label="Job Title"
        v-model="localForm.jobTitleText"
        variant="outlined"
        :error-messages="getErrorMessages('jobTitleText').value"
        @blur="v$.jobTitleText?.$touch()"
        @input="updateForm"
      >
        <template #label>
          Job Title
          <span v-if="isRequired('jobTitleText')" class="ml-1 red--text">
            <strong> *</strong>
          </span>
        </template>
      </v-text-field>
    </template>

    <!-- Common Fields -->
    <v-select
      class="pt-5"
      v-model="localForm.fieldOfEmployment"
      label="Field of employment"
      item-title="name"
      item-value="id"
      variant="outlined"
      :items="companiesSectors"
      :multiple="false"
      :chips="false"
      @update:model-value="updateForm"
    />

    <v-select
      class="pt-5"
      v-model="localForm.typeOfEmployment"
      label="Type of employment"
      item-title="name"
      return-object="true"
      variant="outlined"
      :items="typesOfEmployment"
      :item-props="(item) => ({ disabled: item.deprecated })"
      :multiple="false"
      :chips="false"
      @update:model-value="updateForm"
    >
      <template #selection="{ item }">
        {{ getTypeOfEmploymentDisplay(item.value) }}
      </template>
    </v-select>

    <v-select
      v-model="localForm.durationOfContract"
      label="Duration of Contract"
      item-title="name"
      return-object="true"
      variant="outlined"
      :items="durationsOfContracts"
      :multiple="false"
      :chips="false"
      @update:model-value="updateForm"
    />

    <v-date-input
      v-model="localForm.jobStartDate"
      variant="outlined"
      prepend-icon=""
      placeholder="DD/MM/YYYY"
      prepend-inner-icon="$calendar"
      hide-details="auto"
      :allowed-dates="restrictFutureDates"
      :error-messages="getErrorMessages('jobStartDate').value"
      @blur="v$.jobStartDate?.$touch()"
      @update:model-value="updateForm"
    >
      <template #label>
        Job Start Date
        <span v-if="isRequired('jobStartDate')" class="red--text">
          <strong>* </strong>
        </span>
      </template>
    </v-date-input>

    <v-date-input
      class="pt-5"
      v-model="localForm.jobEndDate"
      variant="outlined"
      prepend-icon=""
      placeholder="DD/MM/YYYY"
      prepend-inner-icon="$calendar"
      hide-details="auto"
      :allowed-dates="restrictFutureDates"
      :error-messages="getErrorMessages('jobEndDate').value"
      @blur="v$.jobEndDate?.$touch()"
      @update:model-value="updateForm"
    >
      <template #label>
        Job End Date
        <span v-if="isRequired('jobEndDate')" class="red--text">
          <strong>* </strong>
        </span>
      </template>
    </v-date-input>

    <v-range-slider
      class="my-slider mt-5"
      v-model="localForm.netSalaryRange"
      :min="380"
      :max="3000"
      step="50"
      label="Net salary range (€)"
      thumb-label="always"
      @update:model-value="updateForm"
    />

    <v-select
      v-model="localForm.socialSecurity"
      :items="booleanOptions"
      item-title="text"
      item-value="value"
      variant="outlined"
      :error-messages="getErrorMessages('socialSecurity').value"
      :is-required="isRequired('socialSecurity')"
      @blur="v$.socialSecurity?.$touch()"
      @update:model-value="updateForm"
    >
      <template #label>
        Social security?
        <span v-if="isRequired('socialSecurity')" class="ml-1 red--text">
          <strong> *</strong>
        </span>
      </template>
    </v-select>
  </v-card-text>
</template>

<script setup>
import { computed, watch, toRefs, reactive, onMounted, toRaw, ref } from "vue";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import { defineProps, defineEmits } from "vue";
import RadioGroup from "../RadioGroup.vue";
import Autocomplete from "../Autocomplete.vue";
import { useListStore } from "@/stores";

const listStore = useListStore();
const jobPositions = computed(() => listStore.jobPositions);
const companies = computed(() => listStore.companies);
const companiesSectors = computed(() => listStore.companiesSectors);

const props = defineProps({
  form: Object, // parent passes full form object
  rules: Object, // parent passes validation rules
});

const emit = defineEmits(["update:form"]);

const localForm = reactive({
  ...props.form,
  netSalaryRange: Array.isArray(props.form.netSalaryRange)
    ? [...props.form.netSalaryRange]
    : [380, 1500],
});

// Filtered job positions based on selected company
const filteredJobPositions = ref([]);

// Computed properties for conditional rendering
const isPartOfNetworkYes = computed(() => {
  return localForm.partOfNetwork?.value === "YES";
});

const isPartOfNetworkNoOrNotSure = computed(() => {
  return (
    localForm.partOfNetwork?.value === "NO" ||
    localForm.partOfNetwork?.value === "NOT_SURE"
  );
});

// Dynamic required fields based on partOfNetwork
const requiredFields = computed(() => {
  const baseFields = [
    "partOfNetwork",
    "jobStartDate",
    "socialSecurity",
  ];

  if (isPartOfNetworkYes.value) {
    return [...baseFields, "company", "jobTitle"];
  } else if (isPartOfNetworkNoOrNotSure.value) {
    return [...baseFields, "employerName", "jobTitleText"];
  }

  return baseFields;
});

const completionPercent = computed(() => {
  const total = requiredFields.value.length;

  const filled = requiredFields.value.filter((field) => {
    return (
      localForm[field] !== null &&
      localForm[field] !== undefined &&
      localForm[field] !== ""
    );
  }).length;

  return Math.round((filled / total) * 100);
});

// Computed validation rules based on partOfNetwork
const validationRules = computed(() => {
  const baseRules = {
    partOfNetwork: { required },
    fieldOfEmployment: {},
    typeOfEmployment: {},
    durationOfContract: {},
    jobStartDate: { required },
    jobEndDate: {},
    socialSecurity: { required },
  };

  if (isPartOfNetworkYes.value) {
    return {
      ...baseRules,
      company: { required },
      jobTitle: { required },
    };
  } else if (isPartOfNetworkNoOrNotSure.value) {
    return {
      ...baseRules,
      employerName: { required },
      jobTitleText: { required },
    };
  }

  return baseRules;
});

const isRequired = (field) => {
  return validationRules.value[field]?.required !== undefined;
};

const v$ = useVuelidate(validationRules, localForm);

const getErrorMessages = (field) =>
  computed(() =>
    v$.value[field]?.$error
      ? v$.value[field].$errors.map((e) => e.$message)
      : []
  );

const restrictFutureDates = (val) => {
  return val <= new Date();
};

const booleanOptions = [
  { text: "Yes", value: true },
  { text: "No", value: false },
];

const booleanNotSureOptions = [
  { name: "Yes", value: "YES" },
  { name: "No", value: "NO" },
  { name: "Not sure", value: "NOT_SURE" },
];

const allTypesOfEmployment = [
  { name: "Full time", value: "FULL_TIME", deprecated: false },
  { name: "Part time", value: "PART_TIME", deprecated: false },
  { name: "Irregular", value: "IRREGULAR", deprecated: false },
  { name: "Freelancer", value: "FREELANCER", deprecated: false },
  { name: "Internship", value: "INTERNSHIP", deprecated: false },
  { name: "No contract (Deprecated)", value: "NO_CONTRACT", deprecated: true },
  { name: "Seasonal (Deprecated)", value: "SEASONAL", deprecated: true },
  { name: "Contract (Deprecated)", value: "CONTRACT", deprecated: true },
  { name: "Other (Deprecated)", value: "OTHER", deprecated: true },
];

const typesOfEmployment = computed(() => {
  const activeOptions = allTypesOfEmployment.filter((t) => !t.deprecated);
  // If editing an old record that has a deprecated value, include it so it displays
  const currentValue = localForm.typeOfEmployment?.value;
  if (currentValue) {
    const deprecatedOption = allTypesOfEmployment.find(
      (t) => t.value === currentValue && t.deprecated
    );
    if (deprecatedOption) {
      return [...activeOptions, deprecatedOption];
    }
  }
  return activeOptions;
});

const getTypeOfEmploymentDisplay = (value) => {
  const match = allTypesOfEmployment.find((t) => t.value === value);
  return match ? match.name : value;
};

const durationsOfContracts = [
  { name: "Indefinite", value: "INDEFINITE" },
  {
    name: "1-month",
    value: "ONE_MONTH",
  },
  { name: "3-month", value: "THREE_MONTH" },
  { name: "6-month", value: "SIX_MONTH" },
  { name: "Other", value: "OTHER" },
];
// Handler for partOfNetwork change
const onPartOfNetworkChange = () => {
  // Clear conditional fields when partOfNetwork changes
  if (isPartOfNetworkYes.value) {
    // Clear text fields
    localForm.employerName = null;
    localForm.jobTitleText = null;
  } else if (isPartOfNetworkNoOrNotSure.value) {
    // Clear company and jobTitle
    localForm.company = null;
    localForm.jobTitle = null;
    filteredJobPositions.value = [];
  }
  updateForm();
};

// Handler for company change - fetch job positions for selected company
const onCompanyChange = async () => {
  if (localForm.company?.id) {
    try {
      await listStore.fetchJobPositions({ company: localForm.company.id });
      filteredJobPositions.value = listStore.jobPositions;
      // Clear job title when company changes
      localForm.jobTitle = null;
    } catch (error) {
      console.error("Error fetching job positions:", error);
      filteredJobPositions.value = [];
    }
  } else {
    filteredJobPositions.value = [];
    localForm.jobTitle = null;
  }
  updateForm();
};

// Emit changes back to parent
const updateForm = () => {
  const plainForm = JSON.parse(JSON.stringify(toRaw(localForm)));
  emit("update:form", plainForm);
};

watch(
  completionPercent,
  (val) => {
    emit("update:completion", val);
  },
  { immediate: true }
);

// Watch parent updates in case they change
watch(
  () => props.form,
  (newVal) => {
    Object.assign(localForm, newVal);
    if (!Array.isArray(localForm.netSalaryRange)) {
      localForm.netSalaryRange = [380, 1500];
    }
  },
  { deep: true, immediate: true }
);

onMounted(async () => {
  await listStore.fetchCompaniesSectors();
  await listStore.fetchCompanies();

  // If editing and company is already selected, load filtered job positions
  if (isPartOfNetworkYes.value && localForm.company?.id) {
    await listStore.fetchJobPositions({ company: localForm.company.id });
    filteredJobPositions.value = listStore.jobPositions;
  } else {
    // Otherwise fetch all job positions for initial load
    await listStore.fetchJobPositions();
  }
});
</script>

<style scoped>
/* 1. Reduce inactive track intensity */
::v-deep(.my-slider .v-slider-track__background) {
  background-color: rgba(255, 181, 90, 0.2) !important;
}

/* 2. Normal active track intensity */
::v-deep(.my-slider .v-slider-track__fill) {
  background-color: rgba(255, 181, 90, 0.5) !important;
}

/* 3. Strong active track when focusing/dragging */
::v-deep(.my-slider.v-slider--focused .v-slider-track__fill) {
  background-color: rgba(255, 181, 90, 1) !important;
}
</style>
